/**
 * Typed client for /audit-log/* — backs the Audit Log screen.
 *
 * Two notable categories the legacy dashboard CATEGORIES array missed:
 * `fleet` and `fleet_compliance`. Both are real on the server (see
 * AuditLogSchema in nurev-api/app/models/auditLog.js) — fleet events
 * have distinct retention (7y for MSA + env-cert vs 2y operational)
 * and a separate compliance-team consumer. Including them here.
 */
import { client } from './client';

export type AuditCategory =
  | 'auth'
  | 'user'
  | 'device'
  | 'payout'
  | 'component'
  | 'system'
  | 'support'
  | 'fleet'
  | 'fleet_compliance';

export type AuditTargetType =
  | 'user'
  | 'appUser'
  | 'withdrawal'
  | 'component'
  | 'system'
  | 'fleet_node'
  | 'fleet_group'
  | 'fleet_provisioning_token'
  | null;

export interface AuditAdminRef {
  _id: string;
  name?: string;
  email?: string;
}

export interface AuditLogEntry {
  _id: string;
  action: string;
  category: AuditCategory;
  adminId?: AuditAdminRef | string | null;
  adminEmail?: string;
  actorPublisherId?: string | null;
  targetType?: AuditTargetType;
  targetId?: string | null;
  details?: Record<string, unknown>;
  ip?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuditLogListResponse {
  docs: AuditLogEntry[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
}

export interface AuditCategoryBucket {
  _id: AuditCategory | null;
  count: number;
}

export interface AuditAdminBucket {
  _id: string | null;
  count: number;
}

export interface AuditStatsResponse {
  totalLogs: number;
  last24h: number;
  byCategory: AuditCategoryBucket[];
  byAdmin: AuditAdminBucket[];
}

export interface AuditLogListParams {
  page?: number;
  limit?: number;
  category?: AuditCategory | '';
  action?: string;
  adminId?: string;
  from?: string;
  to?: string;
}

export async function fetchAuditLogs(
  params: AuditLogListParams
): Promise<AuditLogListResponse> {
  const cleanParams: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== undefined && v !== null) {
      cleanParams[k] = v;
    }
  });
  const r = await client.get<AuditLogListResponse>('/audit-log', {
    params: cleanParams
  });
  return r.data;
}

export async function fetchAuditStats(): Promise<AuditStatsResponse> {
  const r = await client.get<AuditStatsResponse>('/audit-log/stats');
  return r.data;
}
